import React, { useMemo, useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { KPICard } from '../../components/ui/KPICard';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { invoices, Invoice } from '../../data/invoices';
import { ReceiptIcon, AlertTriangleIcon, CheckCircleIcon, PlusIcon, ArrowLeftIcon, DownloadIcon, MessageSquareIcon, FileTextIcon, CreditCardIcon, ClockIcon } from 'lucide-react';
export const InvoicesPage: React.FC = () => {
  const {
    user
  } = useAuth();
  const supplierId = 'SUP-001'; // Default for demo
  const [activeTab, setActiveTab] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  // Filter invoices for this supplier
  const myInvoices = useMemo(() => {
    return invoices.filter(inv => inv.supplierId === supplierId);
  }, [supplierId]);
  // Filter based on active tab
  const filteredInvoices = useMemo(() => {
    if (activeTab === 'all') return myInvoices;
    if (activeTab === 'exceptions') return myInvoices.filter(inv => ['exception', 'rejected'].includes(inv.status));
    if (activeTab === 'pending') return myInvoices.filter(inv => ['submitted', 'under-review'].includes(inv.status));
    if (activeTab === 'approved') return myInvoices.filter(inv => ['approved', 'payment-scheduled', 'paid'].includes(inv.status));
    return myInvoices;
  }, [myInvoices, activeTab]);
  // KPIs
  const exceptionCount = myInvoices.filter(inv => ['exception', 'rejected'].includes(inv.status)).length;
  const pendingCount = myInvoices.filter(inv => ['submitted', 'under-review'].includes(inv.status)).length;
  const scheduledAmount = myInvoices.filter(inv => ['approved', 'payment-scheduled'].includes(inv.status)).reduce((sum, inv) => sum + inv.totalAmount, 0);
  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-SG', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  // Detail View
  if (selectedInvoice) {
    return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-1231">
        <div className="flex items-center gap-2 text-sm text-text-secondary mb-4 cursor-pointer hover:text-fpg-navy transition-colors" onClick={() => setSelectedInvoice(null)} data-id="element-1232">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1233" />
          Back to Invoices
        </div>

        <PageHeader title={`Invoice: ${selectedInvoice.id}`} description={`Submitted on ${selectedInvoice.submittedDate || selectedInvoice.invoiceDate}`} primaryAction={['exception', 'rejected'].includes(selectedInvoice.status) ? <Button variant="primary" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-1236" />} data-id="element-1235">
                Submit Correction
              </Button> : <Button variant="secondary" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1238" />} data-id="element-1237">
                Download PDF
              </Button>} secondaryActions={<Button variant="secondary" leftIcon={<MessageSquareIcon className="w-4 h-4" data-id="element-1240" />} data-id="element-1239">
              Contact AP Support
            </Button>} data-id="element-1234" />

        {['exception', 'rejected'].includes(selectedInvoice.status) && <div className="bg-status-error-bg border border-status-error/20 rounded-fluent-lg p-4 flex items-start gap-3" data-id="element-1241">
            <AlertTriangleIcon className="w-5 h-5 text-status-error mt-0.5 flex-shrink-0" data-id="element-1242" />
            <div data-id="element-1243">
              <h3 className="text-sm font-semibold text-status-error mb-1" data-id="element-1244">
                Invoice Exception
              </h3>
              <p className="text-sm text-text-secondary" data-id="element-1245">
                {selectedInvoice.exceptionType || 'This invoice could not be processed automatically.'}
                Please review and submit a correction or contact support.
              </p>
              <div className="mt-3 flex gap-3" data-id="element-1246">
                <Button variant="secondary" size="sm" data-id="element-1247">
                  Provide Justification
                </Button>
                <Button variant="secondary" size="sm" data-id="element-1248">
                  Create Credit Note
                </Button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1249">
          <Card className="md:col-span-2 space-y-6" data-id="element-1250">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-1251">
              Invoice Details
            </h2>

            <div className="grid grid-cols-2 gap-y-6 gap-x-4" data-id="element-1252">
              <div data-id="element-1253">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1254">
                  Status
                </p>
                <StatusPill status={selectedInvoice.status} data-id="element-1255" />
              </div>
              <div data-id="element-1256">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1257">
                  Total Amount
                </p>
                <p className="text-lg font-bold text-text-primary" data-id="element-1258">
                  {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
                </p>
              </div>
              <div data-id="element-1259">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1260">
                  Invoice Date
                </p>
                <p className="text-sm text-text-primary" data-id="element-1261">
                  {selectedInvoice.invoiceDate}
                </p>
              </div>
              <div data-id="element-1262">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1263">
                  Due Date
                </p>
                <p className="text-sm text-text-primary font-medium" data-id="element-1264">
                  {selectedInvoice.dueDate}
                </p>
              </div>
              <div data-id="element-1265">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1266">
                  Related PO
                </p>
                {selectedInvoice.poId ? <p className="text-sm text-fpg-navy hover:underline cursor-pointer font-medium" data-id="element-1267">
                    {selectedInvoice.poId}
                  </p> : <p className="text-sm text-text-secondary" data-id="element-1268">Non-PO Invoice</p>}
              </div>
              <div data-id="element-1269">
                <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1270">
                  Match Status
                </p>
                <Badge variant={selectedInvoice.matchStatus === 'full-match' ? 'success' : selectedInvoice.matchStatus === 'partial-match' ? 'warning' : selectedInvoice.matchStatus === 'no-match' ? 'error' : 'neutral'} data-id="element-1271">
                  {selectedInvoice.matchStatus.replace('-', ' ').toUpperCase()}
                </Badge>
              </div>
            </div>
          </Card>

          <Card className="space-y-6" data-id="element-1272">
            <h2 className="text-lg font-semibold text-text-primary border-b border-border-light pb-3" data-id="element-1273">
              Payment Info
            </h2>
            <div className="space-y-4" data-id="element-1274">
              {selectedInvoice.paymentRef ? <>
                  <div data-id="element-1275">
                    <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1276">
                      Payment Reference
                    </p>
                    <p className="text-sm text-text-primary font-mono bg-surface-secondary p-1.5 rounded inline-block" data-id="element-1277">
                      {selectedInvoice.paymentRef}
                    </p>
                  </div>
                  {selectedInvoice.paidDate && <div data-id="element-1278">
                      <p className="text-xs text-text-secondary mb-1 uppercase tracking-wider font-semibold" data-id="element-1279">
                        Paid Date
                      </p>
                      <p className="text-sm text-text-primary flex items-center gap-1" data-id="element-1280">
                        <CheckCircleIcon className="w-4 h-4 text-status-success" data-id="element-1281" />
                        {selectedInvoice.paidDate}
                      </p>
                    </div>}
                </> : <div className="text-center py-6" data-id="element-1282">
                  <CreditCardIcon className="w-8 h-8 text-text-tertiary mx-auto mb-2" data-id="element-1283" />
                  <p className="text-sm text-text-secondary" data-id="element-1284">
                    Payment not yet scheduled
                  </p>
                </div>}
            </div>
          </Card>
        </div>
      </div>;
  }
  // List View
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-1285">
      <PageHeader title="Invoices" description="Track invoice status, resolve exceptions, and view payment schedules." breadcrumbs={[{
      label: 'Home',
      href: '/supplier/dashboard'
    }, {
      label: 'Invoices'
    }]} primaryAction={<Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1288" />} data-id="element-1287">
            Create Invoice
          </Button>} data-id="element-1286" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1289">
        <KPICard title="Exceptions" value={exceptionCount.toString()} icon={<AlertTriangleIcon className="w-5 h-5 text-status-error" data-id="element-1291" />} trend={exceptionCount > 0 ? 'down' : 'neutral'} trendValue={exceptionCount > 0 ? 'Action required' : 'All clear'} data-id="element-1290" />
        <KPICard title="Pending Approval" value={pendingCount.toString()} icon={<ClockIcon className="w-5 h-5 text-status-warning" data-id="element-1293" />} data-id="element-1292" />
        <KPICard title="Scheduled for Payment" value={formatCurrency(scheduledAmount, 'SGD')} icon={<ReceiptIcon className="w-5 h-5 text-status-success" data-id="element-1295" />} data-id="element-1294" />
      </div>

      <Card noPadding className="flex flex-col overflow-hidden" data-id="element-1296">
        <div className="px-6 pt-4 border-b border-border-light" data-id="element-1297">
          <Tabs tabs={[{
          id: 'all',
          label: 'All Invoices',
          count: myInvoices.length
        }, {
          id: 'exceptions',
          label: 'Exceptions',
          count: exceptionCount
        }, {
          id: 'pending',
          label: 'Pending',
          count: pendingCount
        }, {
          id: 'approved',
          label: 'Approved/Paid'
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-1298" />
        </div>

        <DataTable data={filteredInvoices} onRowClick={row => setSelectedInvoice(row)} columns={[{
        header: 'Invoice Number',
        accessorKey: 'id',
        sortable: true
      }, {
        header: 'Invoice Date',
        accessorKey: 'invoiceDate',
        sortable: true
      }, {
        header: 'Due Date',
        accessorKey: 'dueDate',
        sortable: true
      }, {
        header: 'PO Reference',
        accessorKey: 'poId'
      }, {
        header: 'Amount',
        align: 'right',
        sortable: true,
        cell: row => <span className="font-medium" data-id="element-1300">
                  {formatCurrency(row.totalAmount, row.currency)}
                </span>
      }, {
        header: 'Status',
        cell: row => <StatusPill status={row.status} data-id="element-1301" />
      }]} data-id="element-1299" />
      </Card>
    </div>;
};